import React, { useEffect, useState } from 'react';
import './DashboardPage.scss';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Calendar } from 'primereact/calendar';
import { Nullable } from 'primereact/ts-helpers';
import type {
  AdminAccountStatusChange,
  AdminUserCountChange,
  AdminUserCountChangesResponse,
  AdminWorkerRuntimeConfig,
} from '@brand24-original/llm-types';
import { AdminMonitorStatsResponse } from '../../../shared/models/admin/dashboard/AdminMonitorStatsResponse';
import {
  getAccountStatusChanges,
  getActiveMonitorStats,
  getActiveUsersStats,
  getConfiguration,
  getDailyRequestStats,
  getUserCountChanges,
  getWorkerRuntimeConfigs,
} from '../../../shared/services/admin/dashboard/AdminDashboardService';
import Loader from '../../../shared/components/common/loader/Loader';
import { AdminConfigurationResponse } from '../../../shared/models/admin/dashboard/AdminConfigurationResponse';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import EmptyPanel from '../../../shared/components/empty-panel/EmptyPanel';
import ModelBox from './components/ModelBox';
import WorkerConfigCard, { WorkerReportMeta } from './components/WorkerConfigCard';
import { SparklesTrend } from '../../../shared/components/sparkles/Sparkles';
import StatTile from '../../../shared/components/stat-tile/StatTile';
import { MODEL_LABELS } from './modelLabels';
import { formatCount } from './formatCount';
import { MonitorType } from '../../../shared/types/monitor/MonitorType';

// ESCALATION = the cron moved an already non-paying account on (OVERDUE → BLOCKED → DISABLED); it left PAID earlier.
const REASON_LABEL: Record<AdminAccountStatusChange['reason'], string> = {
  CANCELED: 'Canceled (Paddle)',
  PAST_DUE: 'Payment failed',
  ESCALATION: 'Blocked/disabled by cron (left PAID earlier)',
};

// Today's movement against yesterday (backend: today − ups + downs, no history table). Zero sides are hidden.
const CountChange: React.FC<{ change: AdminUserCountChange }> = ({ change }) => (
  <div className="dashboard__count-change">
    {change.up > 0 && (
      <div className="tag tag--stats tag--md" title="New since yesterday">
        <span className="ico--chart-up" />
        <span className="tag__value">+{formatCount(change.up)}</span>
      </div>
    )}
    {change.down > 0 && (
      <div className="tag tag--stats tag--md" title="Lost since yesterday">
        <span className="ico--chart-down" />
        <span className="tag__value">−{formatCount(change.down)}</span>
      </div>
    )}
  </div>
);

// Net change of the day picks the sparkles' colour: up → green, down → red, none → neutral grey.
const trendOf = (change: AdminUserCountChange | null | undefined): SparklesTrend => {
  const net = change ? change.up - change.down : 0;
  if (net > 0) {
    return 'rising';
  }
  return net < 0 ? 'falling' : 'neutral';
};

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [activeMonitors, setActiveMonitors] = useState<number | null>(null);
  const [users, setUsers] = useState<{
    disabled: number;
    blocked: number;
    trial: number;
    paid: number;
    unpaid: number;
  } | null>(null);
  const [countChanges, setCountChanges] = useState<AdminUserCountChangesResponse | null>(null);
  const [configuration, setConfiguration] = useState<AdminConfigurationResponse[] | null>(null);
  const [workerConfigs, setWorkerConfigs] = useState<AdminWorkerRuntimeConfig[] | null>(null);
  const [stats, setStats] = useState<AdminMonitorStatsResponse[]>([]);
  const [statusChanges, setStatusChanges] = useState<AdminAccountStatusChange[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  // Each section shows its own loader and fills in as soon as its request returns — a slow query
  // (models) no longer holds back the counters.
  const [statsLoading, setStatsLoading] = useState(true);
  const [statusChangesLoading, setStatusChangesLoading] = useState(true);

  const findStats = (type: string) => stats.find((s) => s.monitorType === type) || null;
  const isToday = format(selectedDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd');

  const loadDay = (date: Date) => {
    const day = format(date, 'yyyy-MM-dd');
    setStatsLoading(true);
    setStatusChangesLoading(true);
    getDailyRequestStats(day).then((data) => {
      setStats(Array.isArray(data) ? data : []);
      setStatsLoading(false);
    });
    getAccountStatusChanges(day).then((changes) => {
      setStatusChanges(Array.isArray(changes) ? changes : []);
      setStatusChangesLoading(false);
    });
  };

  useEffect(() => {
    // The service helpers resolve with the error object on failure, hence the shape checks.
    Promise.all([getActiveUsersStats(), getActiveMonitorStats()]).then(
      ([usersData, activeMonitorsData]) => {
        setUsers(
          usersData && 'paid' in usersData
            ? usersData
            : { blocked: 0, trial: 0, disabled: 0, paid: 0, unpaid: 0 }
        );
        setActiveMonitors(typeof activeMonitorsData === 'number' ? activeMonitorsData : 0);
      }
    );
    getUserCountChanges().then((data) => setCountChanges(data?.paid ? data : null));
    getConfiguration().then((data) => setConfiguration(Array.isArray(data) ? data : []));
    getWorkerRuntimeConfigs().then((data) => setWorkerConfigs(Array.isArray(data) ? data : []));
    loadDay(new Date());
  }, []);

  const isEnabled = (feature: string) =>
    !!configuration?.find((data) => data.feature === feature)?.isEnabled;

  const counters = [
    {
      title: 'Paid users',
      value: users?.paid || 0,
      icon: 'ico--circle-check',
      change: countChanges?.paid,
    },
    { title: 'Trial users', value: users?.trial || 0, icon: 'ico--zap', change: countChanges?.trial },
    { title: 'Blocked users', value: users?.blocked || 0, icon: 'ico--pause' },
    { title: 'Disabled users', value: users?.disabled || 0, icon: 'ico--close' },
    { title: 'Payment problems', value: users?.unpaid || 0, icon: 'ico--warning' },
    { title: 'Active monitors', value: activeMonitors || 0, icon: 'ico--activity' },
  ];

  // Only switches the code actually reads — SLACK gates worker alerts (SlackService), PROJECT_MIGRATION gates
  // the tech migration crons. The other `configuration` rows (SEMRUSH, BROWSER, GEMINI*, CLAUDE, BING, …) are
  // read nowhere, so a tile for them would show a state that changes nothing.
  const integrations = [
    { label: 'Slack alerts', feature: 'SLACK', icon: 'ico--bell' },
    { label: t('statsMigration'), feature: 'PROJECT_MIGRATION', icon: 'ico--refresh' },
  ];

  const models = Object.entries(MODEL_LABELS).map(([type, header]) => ({
    type: type as MonitorType,
    header,
  }));

  return (
    <div className="dashboard">
      <div
        className={`stat-tiles dashboard__section${
          users === null ? ' dashboard__section--loading' : ''
        }`}
      >
        {users === null && <Loader />}
        {users !== null &&
          counters.map((counter) => (
            <StatTile
              key={counter.title}
              title={counter.title}
              value={formatCount(counter.value)}
              icon={counter.icon}
              trend={trendOf('change' in counter ? counter.change : undefined)}
            >
              {'change' in counter && counter.change && <CountChange change={counter.change} />}
            </StatTile>
          ))}
      </div>

      <div className="page-box__title">Tools &amp; integrations</div>
      <div
        className={`dashboard__integrations dashboard__section${
          configuration === null ? ' dashboard__section--loading' : ''
        }`}
      >
        {configuration === null && <Loader />}
        {configuration !== null &&
          integrations.map((integration) => {
            const isOn = isEnabled(integration.feature);
            return (
              <div
                key={integration.label}
                className={`integration-tile ${isOn ? 'integration-tile--on' : 'integration-tile--off'}`}
              >
                <span className={`ico ico--sm ${integration.icon} integration-tile__icon`} />
                <span className="integration-tile__label">{integration.label}</span>
                <span className="integration-tile__status">{isOn ? 'ON' : 'OFF'}</span>
              </div>
            );
          })}
      </div>

      <div className="page-box__title">
        Processed models
        <Calendar
          value={selectedDate}
          onChange={(e) => {
            const date = e.value as Nullable<Date>;
            if (date) {
              setSelectedDate(date);
              loadDay(date);
            }
          }}
          dateFormat="yy-mm-dd"
          maxDate={new Date()}
          readOnlyInput
          // Touch: the date panel opens as a centred modal with touch-sized cells instead of a tiny popover.
          touchUI={window.matchMedia('(pointer: coarse)').matches}
          pt={{
            root: { className: 'calendar' },
            panel: { className: 'calendar-panel' },
          }}
        />
      </div>

      <div
        className={`dashboard__models dashboard__section${
          statsLoading ? ' dashboard__section--loading' : ''
        }`}
      >
        {statsLoading && <Loader />}
        {models.map((model) => (
          <ModelBox
            key={model.type}
            monitorType={model.type}
            stats={findStats(model.type)}
            header={model.header}
            isToday={isToday}
          />
        ))}
      </div>

      {/* Shown only when the day has lost subscriptions (or while it loads) — an empty box is noise. */}
      {(statusChangesLoading || statusChanges.length > 0) && (
        <>
          <div className="page-box__title">Lost subscriptions</div>
          <div
            className={`dashboard__section${statusChangesLoading ? ' dashboard__section--loading' : ''}`}
          >
            {statusChangesLoading && <Loader />}
            <DataTable
              value={statusChanges}
              dataKey="accountId"
              tableClassName="table"
              className="table-wrapper table-wrapper--stack"
              responsiveLayout="stack"
              tableStyle={{ width: '100%' }}
              rowClassName={(change: AdminAccountStatusChange) =>
                change.reason === 'ESCALATION' ? 'status-changes__row--escalation' : ''
              }
              stripedRows
              emptyMessage={() => <EmptyPanel />}
            >
              <Column
                header="Time"
                headerClassName="table__header"
                style={{ width: '90px' }}
                body={(change: AdminAccountStatusChange) =>
                  format(new Date(change.canceledAt ?? change.statusChangedAt), 'HH:mm')
                }
              />
              <Column
                header="Account"
                headerClassName="table__header"
                bodyClassName="table__cell--stack-title"
                body={(change: AdminAccountStatusChange) => (
                  <>
                    {change.email} <span className="status-changes__id">#{change.accountId}</span>
                  </>
                )}
              />
              <Column field="accountType" header="Plan" headerClassName="table__header" />
              <Column
                header="Reason"
                headerClassName="table__header"
                body={(change: AdminAccountStatusChange) => REASON_LABEL[change.reason]}
              />
              <Column field="accountStatus" header="Status now" headerClassName="table__header" />
            </DataTable>
          </div>
        </>
      )}

      <div className="page-box__title">
        Worker configuration
        {workerConfigs?.length === 1 && <WorkerReportMeta worker={workerConfigs[0]} />}
      </div>
      <div
        className={`dashboard__section${workerConfigs === null ? ' dashboard__section--loading' : ''}`}
      >
        {workerConfigs === null && <Loader />}
        {workerConfigs !== null && workerConfigs.length === 0 && (
          <div className="worker-config worker-config--empty">
            <EmptyPanel
              size="xs"
              textKey="No worker online"
              descriptionKey="Workers report their config on start and every 5 minutes. Start llm-worker, or deploy a version with this feature."
            />
          </div>
        )}
        {workerConfigs?.map((worker) => (
          <WorkerConfigCard
            key={worker.instance}
            worker={worker}
            showInstance={workerConfigs.length > 1}
          />
        ))}
      </div>
    </div>
  );
};

export default DashboardPage;
